import React, { useEffect, useRef, useState } from 'react';
import {
    Table, Input, Tag, Space, Typography, message,
    Modal, Form, Select, Popconfirm, Button, Tooltip, Avatar, Upload, Switch,
} from 'antd';
import {
    SearchOutlined, IdcardOutlined, DeleteOutlined, ReloadOutlined,
    PlusOutlined, SyncOutlined, InfoCircleOutlined, DownloadOutlined, UserOutlined,
    LoadingOutlined, KeyOutlined, UserSwitchOutlined,
} from '@ant-design/icons';
import dayjs from '_helpers/datetime';
import { useFetchWrapper } from '_helpers';
import { useCapabilities } from '_helpers/capabilities';
import { ROLES } from '_helpers/roles';
import { formatSlots } from '_helpers/schedule';
import { StudentProfileCard } from '_components/studentInfoList';

const { Title, Text } = Typography;
const { Option } = Select;

const viMap = {'à':'a','á':'a','ả':'a','ã':'a','ạ':'a','ă':'a','ắ':'a','ằ':'a','ẳ':'a','ẵ':'a','ặ':'a','â':'a','ấ':'a','ầ':'a','ẩ':'a','ẫ':'a','ậ':'a','è':'e','é':'e','ẻ':'e','ẽ':'e','ẹ':'e','ê':'e','ế':'e','ề':'e','ể':'e','ễ':'e','ệ':'e','ì':'i','í':'i','ỉ':'i','ĩ':'i','ị':'i','ò':'o','ó':'o','ỏ':'o','õ':'o','ọ':'o','ô':'o','ố':'o','ồ':'o','ổ':'o','ỗ':'o','ộ':'o','ơ':'o','ớ':'o','ờ':'o','ở':'o','ỡ':'o','ợ':'o','ù':'u','ú':'u','ủ':'u','ũ':'u','ụ':'u','ư':'u','ứ':'u','ừ':'u','ử':'u','ữ':'u','ự':'u','ỳ':'y','ý':'y','ỷ':'y','ỹ':'y','ỵ':'y','đ':'d'};
const norm = s => (s || '').toLowerCase().split('').map(c => viMap[c] || c).join('');
const lastNameKey = name => norm((name || '').trim().split(' ').pop());

/**
 * Default row order: real students by last name, học thử last.
 *
 * Applied to the data rather than folded into the name column's sorter, because a comparator would
 * invert when that column is sorted descending (trials jump to the top) and be bypassed entirely when
 * sorting by another column — sort by ID and "HT" sorts before "ME", the exact opposite. This holds
 * only while no column sort is active; an explicit sort is the user asking for something else.
 */
const trialLast = rows => [...rows].sort((a, b) => {
    const ta = a.enrollment_status === 'trial' ? 1 : 0;
    const tb = b.enrollment_status === 'trial' ? 1 : 0;
    if (ta !== tb) return ta - tb;
    const ka = lastNameKey(a.name), kb = lastNameKey(b.name);
    return ka < kb ? -1 : ka > kb ? 1 : 0;
});

const CELL_STYLE = {
    cursor: 'text',
    borderRadius: 4,
    padding: '1px 4px',
    minHeight: 22,
    display: 'block',
};

export { AllStudentsPage };

function AllStudentsPage() {
    const fetchWrapper = useFetchWrapper();
    const { canResetPasswords } = useCapabilities();   // manager/headmaster only
    const userData = JSON.parse(localStorage.getItem('userData'));
    const [students,      setStudents]      = useState([]);
    const [allClasses,    setAllClasses]    = useState([]);
    const [loading,       setLoading]       = useState(false);
    const [search,        setSearch]        = useState('');

    // ── inline edit ───────────────────────────────────────────────────────────
    const [editCell,  setEditCell]  = useState(null); // { _id, field, value }
    const [saving,    setSaving]    = useState(false);
    // No column sort by default — the default order comes from trialLast() instead, which antd would
    // otherwise re-sort away. Same visible order as before (name ascending), minus the sort arrow.
    const [sortedInfo, setSortedInfo] = useState({});

    // ── responsive ───────────────────────────────────────────────────────────
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    useEffect(() => {
        const handler = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener('resize', handler);
        return () => window.removeEventListener('resize', handler);
    }, []);

    // ── class management ──────────────────────────────────────────────────────
    const [addingClassFor, setAddingClassFor] = useState(null); // student _id

    // ── profile card modal ────────────────────────────────────────────────────
    const [profileVisible,   setProfileVisible]   = useState(false);
    const [profileTarget,    setProfileTarget]    = useState(null);
    const [profileClassInfo, setProfileClassInfo] = useState(null);
    const [profileArchivedClasses, setProfileArchivedClasses] = useState([]);

    // ── create modal ──────────────────────────────────────────────────────────
    const [createVisible, setCreateVisible] = useState(false);
    const [createLoading, setCreateLoading] = useState(false);
    const [avatarUploading, setAvatarUploading] = useState(() => new Set()); // student _ids mid-upload
    const [createForm] = Form.useForm();

    // ── Load students ─────────────────────────────────────────────────────────
    async function load() {
        setLoading(true);
        try {
            const res  = await fetchWrapper.get('/api/manager/students', null, null);
            const json = await res.json();
            if (json.status === 'Success') {
                setStudents(Array.isArray(json.message) ? json.message : []);
            } else {
                message.error('Lỗi: ' + (typeof json.message === 'string' ? json.message : JSON.stringify(json.message)));
            }
        } catch (e) {
            message.error('Không thể tải danh sách học sinh');
        }
        setLoading(false);
    }

    // ── Load all classes (for the add-to-class dropdown) ─────────────────────
    async function loadAllClasses() {
        try {
            const res  = await fetchWrapper.get('/api/manager/classes', null, null);
            const json = await res.json();
            if (json.status === 'Success') {
                setAllClasses(Array.isArray(json.message) ? json.message : []);
            }
        } catch {}
    }

    useEffect(() => {
        load();
        loadAllClasses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ── Inline editing ────────────────────────────────────────────────────────
    function startEdit(record, field, initialValue) {
        if (saving) return;
        setEditCell({ _id: record._id, field, value: initialValue });
    }

    async function saveEdit(cell) {
        if (!cell || saving) return;
        setSaving(true);
        try {
            const body = new URLSearchParams();
            body.append(cell.field, cell.value);
            const res  = await fetchWrapper.put(
                `/api/manager/students/${cell._id}`,
                'application/x-www-form-urlencoded',
                body
            );
            const json = await res.json();
            if (json.status === 'Success') {
                setStudents(prev => prev.map(s =>
                    s._id === cell._id ? { ...s, [cell.field]: cell.value } : s
                ));
            } else {
                message.error(json.message || 'Lỗi khi lưu');
            }
        } catch (e) {
            message.error('Lỗi khi lưu: ' + e.message);
        }
        setSaving(false);
        setEditCell(null);
    }

    function editableText(record, field, rawValue, bold) {
        const active = editCell?._id === record._id && editCell?.field === field;
        if (active) {
            return (
                <Input
                    autoFocus
                    size="small"
                    value={editCell.value}
                    onChange={e => setEditCell({ ...editCell, value: e.target.value })}
                    onBlur={() => saveEdit(editCell)}
                    onPressEnter={() => saveEdit(editCell)}
                    style={{ minWidth: 80, fontWeight: bold ? 600 : undefined }}
                />
            );
        }
        return (
            <Tooltip title="Nhấp đúp để chỉnh sửa" mouseEnterDelay={0.8}>
                <span
                    className="editable-cell"
                    style={{ ...CELL_STYLE, fontWeight: bold ? 600 : undefined }}
                    onDoubleClick={() => startEdit(record, field, rawValue || '')}
                >
                    {rawValue || <Text type="secondary">—</Text>}
                </span>
            </Tooltip>
        );
    }

    const STATUS_OPTIONS = [
        { value: 'active',    label: 'Đang học', tone: 'ok'      },
        { value: 'inactive',  label: 'Kết thúc', tone: 'bad'     },
        { value: 'suspended', label: 'Bảo lưu',  tone: 'neutral' },
        { value: 'trial',     label: 'Học thử',  tone: 'info'    },
    ];

    function editableStatus(record) {
        const active = editCell?._id === record._id && editCell?.field === 'enrollment_status';
        if (active) {
            return (
                <Select
                    autoFocus
                    defaultOpen
                    size="small"
                    value={editCell.value}
                    style={{ width: '100%' }}
                    onChange={v => {
                        const next = { ...editCell, value: v };
                        setEditCell(next);
                        saveEdit(next);
                    }}
                    onBlur={() => saveEdit(editCell)}
                >
                    {STATUS_OPTIONS.map(o => (
                        <Option key={o.value} value={o.value}>{o.label}</Option>
                    ))}
                </Select>
            );
        }
        const opt = STATUS_OPTIONS.find(o => o.value === (record.enrollment_status || 'active'));
        return (
            <Tooltip title="Nhấp đúp để chỉnh sửa" mouseEnterDelay={0.8}>
                <span
                    className="editable-cell"
                    style={CELL_STYLE}
                    onDoubleClick={() => startEdit(record, 'enrollment_status', record.enrollment_status || 'active')}
                >
                    <span className={`tag t-${opt?.tone || 'ok'}`}>{opt?.label || 'Đang học'}</span>
                </span>
            </Tooltip>
        );
    }

    function editableGender(record) {
        const active = editCell?._id === record._id && editCell?.field === 'gender';
        if (active) {
            return (
                <Select
                    autoFocus
                    defaultOpen
                    size="small"
                    value={editCell.value}
                    style={{ width: '100%' }}
                    onChange={v => {
                        const next = { ...editCell, value: v || '' };
                        setEditCell(next);
                        saveEdit(next);
                    }}
                    onBlur={() => saveEdit(editCell)}
                    allowClear
                >
                    <Option value="male">Nam</Option>
                    <Option value="female">Nữ</Option>
                </Select>
            );
        }
        const display = record.gender === 'male' ? 'Nam' : record.gender === 'female' ? 'Nữ' : null;
        return (
            <Tooltip title="Nhấp đúp để chỉnh sửa" mouseEnterDelay={0.8}>
                <span
                    className="editable-cell"
                    style={CELL_STYLE}
                    onDoubleClick={() => startEdit(record, 'gender', record.gender || '')}
                >
                    {display || <Text type="secondary">—</Text>}
                </span>
            </Tooltip>
        );
    }

    // ── Class management ──────────────────────────────────────────────────────
    async function removeFromClass(record, classId) {
        try {
            const body = new URLSearchParams();
            body.append('members', `["${record.student_id}"]`);
            const res  = await fetchWrapper.delete(
                `/api/manager/classes/${classId}/members/delete`,
                'application/x-www-form-urlencoded',
                body
            );
            const json = await res.json();
            if (json.status === 'Success') {
                setStudents(prev => prev.map(s =>
                    s._id === record._id
                        ? { ...s, classes: (s.classes || []).filter(c => c.class_id !== classId) }
                        : s
                ));
                message.success('Đã xóa học sinh khỏi lớp');
            } else {
                message.error(json.message || 'Không thể xóa khỏi lớp');
            }
        } catch (e) {
            message.error('Lỗi: ' + e.message);
        }
    }

    // Warn — never block — when a học thử student is put in a real class, or vice versa. The teacher
    // is the one who pays for it: a trial student on a real roster appears on every attendance sheet
    // and must be marked each lesson, or the 22:00 alert fires and the class's completion % drops.
    function confirmMixing(record, cls) {
        const studentTrial = record.enrollment_status === 'trial';
        if (studentTrial === !!cls?.is_trial) return Promise.resolve(true);
        const [title, content] = studentTrial
            ? ['Thêm học sinh học thử vào lớp chính thức?',
               `${record.name} đang là học thử. Học sinh sẽ xuất hiện trong mọi buổi điểm danh của lớp này và giáo viên phải điểm danh mỗi buổi, nhưng chưa có gói học phí. Nên chuyển thành học sinh chính thức trước.`]
            : ['Thêm học sinh chính thức vào lớp học thử?',
               `${record.name} không phải học thử. Lớp học thử không điểm danh học sinh, nên buổi học của lớp này sẽ không được tính vào số buổi đã học của em.`];
        return new Promise(resolve => Modal.confirm({
            title, content, okText: 'Vẫn thêm', cancelText: 'Hủy',
            onOk: () => resolve(true), onCancel: () => resolve(false),
        }));
    }

    async function addToClass(record, classId) {
        setAddingClassFor(null);
        if (!(await confirmMixing(record, allClasses.find(c => c.class_id === classId)))) return;
        try {
            const body = new URLSearchParams();
            body.append('members', `["${record.student_id}"]`);
            const res  = await fetchWrapper.post(
                `/api/manager/classes/${classId}/members/add`,
                'application/x-www-form-urlencoded',
                body
            );
            const json = await res.json();
            if (json.status === 'Success') {
                const cls = allClasses.find(c => c.class_id === classId);
                if (cls) {
                    setStudents(prev => prev.map(s =>
                        s._id === record._id
                            ? { ...s, classes: [...(s.classes || []), { class_id: cls.class_id, class_name: cls.class_name, archived: !!cls.archived }] }
                            : s
                    ));
                }
                message.success('Đã thêm học sinh vào lớp');
            } else {
                message.error(json.message || 'Không thể thêm vào lớp');
            }
        } catch (e) {
            message.error('Lỗi: ' + e.message);
        }
    }

    // ── Profile card update callback (student fields) ─────────────────────────
    function handleProfileUpdate(updates) {
        if (!profileTarget) return;
        const updated = { ...profileTarget, ...updates };
        setProfileTarget(updated);
        setStudents(prev => prev.map(s =>
            s._id === profileTarget._id ? { ...s, ...updates } : s
        ));
    }

    // ── Profile card class update callback ────────────────────────────────────
    function handleClassUpdate(classId, updates) {
        setAllClasses(prev => prev.map(c =>
            c.class_id === classId ? { ...c, ...updates } : c
        ));
        setProfileClassInfo(prev => prev ? { ...prev, ...updates } : prev);
    }

    // ── CSV export ────────────────────────────────────────────────────────────
    function exportCSV() {
        const headers = [
            'Họ và tên', 'Mã học sinh', 'Giới tính', 'Ngày sinh',
            'Email', 'SĐT học sinh', 'SĐT phụ huynh', 'Họ tên phụ huynh', 'Địa chỉ',
            'Lớp', 'Giờ học', 'Người take care', 'Sách',
            'Ngày bắt đầu', 'Ngày kết thúc',
        ];
        const rows = students.map(s => {
            // Export the current (non-archived) class, not an old one.
            const firstCls = (s.classes || []).find(c => !c.archived) || s.classes?.[0];
            const clsInfo  = firstCls ? allClasses.find(c => c.class_id === firstCls.class_id) : null;
            return [
                s.name          || '',
                s.student_id    || '',
                s.gender === 'male' ? 'Nam' : s.gender === 'female' ? 'Nữ' : '',
                s.date_of_birth ? dayjs(s.date_of_birth).format('DD/MM/YYYY') : '',
                s.email         || '',
                s.phone_number  || '',
                s.parent_number || '',
                s.parent_name   || '',
                s.location      || '',
                firstCls?.class_name        || '',
                formatSlots(clsInfo?.schedule_slots),
                clsInfo?.caretaker          || '',
                clsInfo?.book               || '',
                clsInfo?.start_date ? dayjs(clsInfo.start_date).format('DD/MM/YYYY') : '',
                clsInfo?.end_date   ? dayjs(clsInfo.end_date).format('DD/MM/YYYY')   : '',
            ];
        });
        const csvContent = [headers, ...rows]
            .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
            .join('\n');
        const blob = new Blob(['﻿' + csvContent], { type: 'text/csv;charset=utf-8;' });
        const url  = URL.createObjectURL(blob);
        const a    = document.createElement('a');
        a.href     = url;
        a.download = `danh_sach_hoc_sinh_${dayjs().format('YYYY-MM-DD')}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    }

    // ── ID Generator ──────────────────────────────────────────────────────────
    // Trial students draw from a separate HT26xxx space, so the previewed id has to follow the toggle.
    async function fetchNewId(trial = false) {
        try {
            const res  = await fetchWrapper.get(`/api/manager/students/generate-id${trial ? '?trial=1' : ''}`, null, null);
            const json = await res.json();
            if (json.status === 'Success') return json.message.student_id;
        } catch {}
        return '';
    }

    async function openCreate() {
        const id = await fetchNewId(false);
        createForm.resetFields();
        createForm.setFieldsValue({ student_id: id, is_trial: false });
        setCreateVisible(true);
    }

    async function onCreateTrialToggle(checked) {
        createForm.setFieldsValue({ student_id: await fetchNewId(checked) });
    }

    async function regenerateId() {
        const id = await fetchNewId(!!createForm.getFieldValue('is_trial'));
        createForm.setFieldsValue({ student_id: id });
    }

    async function handleCreateSave() {
        let values;
        try { values = await createForm.validateFields(); } catch { return; }
        setCreateLoading(true);
        try {
            const body = new URLSearchParams();
            body.append('student_id',    values.student_id);
            body.append('name',          values.name);
            body.append('parent_number', values.parent_number || '');
            body.append('gender',        values.gender        || '');
            body.append('phone_number',  values.phone_number  || '');
            body.append('email',         values.email         || '');
            body.append('enrollment_status', values.is_trial ? 'trial' : 'active');
            const res  = await fetchWrapper.post('/api/manager/students', 'application/x-www-form-urlencoded', body);
            const json = await res.json();
            if (json.status === 'Success') {
                message.success(`Đã tạo ${values.is_trial ? 'học sinh học thử' : 'học sinh'} "${values.name}" — ID & mật khẩu: ${values.student_id}`);
                setCreateVisible(false);
                createForm.resetFields();
                await load();
            } else {
                message.error(json.message || 'Tạo tài khoản thất bại');
            }
        } catch (e) {
            message.error('Lỗi: ' + e.message);
        }
        setCreateLoading(false);
    }

    // ── Avatar upload (staff → student) ───────────────────────────────────────
    async function uploadAvatar(record, file) {
        const fd = new FormData();
        fd.append('file', file);
        setAvatarUploading(prev => new Set(prev).add(record._id));
        const hide = message.loading('Đang tải ảnh đại diện...', 0);
        try {
            const res  = await fetchWrapper.post(`/api/users/${record._id}/avatar`, null, fd);
            const json = await res.json();
            if (json.status === 'Success') {
                setStudents(prev => prev.map(s =>
                    s._id === record._id ? { ...s, avatar: json.message.avatar } : s));
                message.success('Đã cập nhật ảnh đại diện');
            } else {
                message.error(typeof json.message === 'string' ? json.message : 'Tải ảnh thất bại');
            }
        } catch (e) {
            message.error('Lỗi tải ảnh: ' + e.message);
        } finally {
            hide();
            setAvatarUploading(prev => { const n = new Set(prev); n.delete(record._id); return n; });
        }
    }

    // ── Bulk avatar upload (match photo filename → student_id) ────────────────
    // One-time job: name each photo after a student id (e.g. ME261.jpg), select
    // many at once, we match by id and reuse the per-student avatar endpoint.
    const bulkInputRef = useRef();
    const [bulkBusy, setBulkBusy] = useState(false);

    async function handleBulkFiles(e) {
        const files = Array.from(e.target.files || []);
        e.target.value = ''; // let the same files be re-picked later
        if (!files.length) return;

        const byId = new Map(students.map(s => [(s.student_id || '').toUpperCase(), s]));
        const matched = [], unmatched = [];
        for (const f of files) {
            const id = f.name.replace(/\.[^.]+$/, '').trim().toUpperCase();
            const rec = byId.get(id);
            if (rec) matched.push({ rec, f }); else unmatched.push(f.name);
        }
        if (!matched.length) {
            message.warning('Không khớp được ảnh nào. Đặt tên file theo mã học sinh, ví dụ ME261.jpg');
            return;
        }

        setBulkBusy(true);
        const hide = message.loading(`Đang tải ${matched.length} ảnh...`, 0);
        let ok = 0, fail = 0;
        const updates = {};
        for (const { rec, f } of matched) { // sequential — one-time job, keeps the VPS calm
            try {
                const fd = new FormData();
                fd.append('file', f);
                const res  = await fetchWrapper.post(`/api/users/${rec._id}/avatar`, null, fd);
                const json = await res.json();
                if (json.status === 'Success') { ok++; updates[rec._id] = json.message.avatar; }
                else fail++;
            } catch { fail++; }
        }
        hide();
        setBulkBusy(false);
        setStudents(prev => prev.map(s => updates[s._id] ? { ...s, avatar: updates[s._id] } : s));
        Modal.info({
            title: 'Kết quả tải ảnh hàng loạt',
            content: (
                <div>
                    <p>Thành công: <b>{ok}</b>{fail > 0 && <> · Thất bại: <b>{fail}</b></>}</p>
                    {unmatched.length > 0 && (
                        <>
                            <p>Không khớp mã học sinh ({unmatched.length}):</p>
                            <div style={{ maxHeight: 160, overflow: 'auto', fontSize: 12, color: 'var(--ink-4)' }}>
                                {unmatched.join(', ')}
                            </div>
                        </>
                    )}
                </div>
            ),
        });
    }

    // ── Reset password to default (= student_id) ──────────────────────────────
    async function handleResetPassword(record) {
        try {
            const res  = await fetchWrapper.post(`/api/manager/users/${record._id}/reset-password`, null, null);
            const json = await res.json();
            if (json.status === 'Success') message.success(`Đã đặt lại mật khẩu: ${json.message.default}`);
            else message.error(typeof json.message === 'string' ? json.message : 'Đặt lại thất bại');
        } catch (e) {
            message.error('Lỗi: ' + e.message);
        }
    }

    // ── Học thử → học sinh chính thức ─────────────────────────────────────────
    // The new id and password only exist in this response — the student is logged out and can't be
    // told by the app, so the dialog stays open until the manager dismisses it. Modal.success, not a
    // toast, for exactly that reason.
    async function handleConvert(record) {
        try {
            const res  = await fetchWrapper.post(`/api/manager/students/${record._id}/convert`, null, null);
            const json = await res.json();
            if (json.status === 'Success') {
                Modal.success({
                    title: 'Đã chuyển thành học sinh chính thức',
                    content: (
                        <div>
                            <p style={{ marginBottom: 16 }}>
                                <span style={{ fontWeight: 600 }}>{record.name}</span> giờ là học sinh chính thức.
                            </p>
                            <div style={{ display: 'flex', gap: 4, flexDirection: 'column', marginBottom: 16 }}>
                                <span>Mã học sinh mới: <span style={{ fontWeight: 600 }}>{json.message.student_id}</span></span>
                                <span>Mật khẩu mới: <span style={{ fontWeight: 600 }}>{json.message.password}</span></span>
                            </div>
                            <p style={{ color: 'var(--ink-4)', fontSize: 12, marginBottom: 0 }}>
                                Học sinh đã bị đăng xuất và mã cũ ({record.student_id}) không dùng được nữa —
                                hãy báo lại thông tin đăng nhập mới cho phụ huynh.
                            </p>
                        </div>
                    ),
                });
                await load();
            } else {
                message.error(typeof json.message === 'string' ? json.message : 'Chuyển thất bại');
            }
        } catch (e) {
            message.error('Lỗi: ' + e.message);
        }
    }

    // ── Delete student ────────────────────────────────────────────────────────
    async function handleDelete(record) {
        try {
            const res  = await fetchWrapper.delete(`/api/manager/students/${record._id}`, null, null);
            const json = await res.json();
            if (json.status === 'Success') {
                message.success('Đã xóa học sinh');
                setStudents(prev => prev.filter(s => s._id !== record._id));
            } else {
                message.error(json.message || 'Xóa thất bại');
            }
        } catch (e) {
            message.error('Lỗi: ' + e.message);
        }
    }

    // ── Filter / stats ────────────────────────────────────────────────────────
    const filtered = students.filter(s => {
        const q = norm(search);
        return (
            norm(s.name).includes(q) ||
            (s.student_id   || '').toLowerCase().includes(q) ||
            (s.phone_number || '').toLowerCase().includes(q)
        );
    });

    const withClass    = students.filter(s => s.classes && s.classes.length > 0).length;
    const withoutClass = students.length - withClass;
    const countActive    = students.filter(s => !s.enrollment_status || s.enrollment_status === 'active').length;
    const countInactive  = students.filter(s => s.enrollment_status === 'inactive').length;
    const countSuspended = students.filter(s => s.enrollment_status === 'suspended').length;
    const countTrial     = students.filter(s => s.enrollment_status === 'trial').length;

    // ── Columns ───────────────────────────────────────────────────────────────
    const columns = [
        {
            title: 'Ảnh',
            key: 'avatar',
            width: 56,
            fixed: 'left',
            align: 'center',
            render: (_, record) => (
                <Upload
                    showUploadList={false}
                    accept="image/*"
                    disabled={avatarUploading.has(record._id)}
                    beforeUpload={file => { uploadAvatar(record, file); return false; }}
                >
                    <Tooltip title={avatarUploading.has(record._id) ? 'Đang tải...' : 'Tải ảnh đại diện'}>
                        <span style={{ cursor: avatarUploading.has(record._id) ? 'wait' : 'pointer' }}>
                            {avatarUploading.has(record._id)
                                ? <Avatar size={36} icon={<LoadingOutlined />} />
                                : record.avatar
                                    ? <Avatar src={`/api/photos/${record.avatar}`} size={36} />
                                    : <Avatar size={36} icon={<UserOutlined />} />}
                        </span>
                    </Tooltip>
                </Upload>
            ),
        },
        {
            title: 'Họ và tên',
            dataIndex: 'name',
            key: 'name',
            width: isMobile ? 200 : 180,
            fixed: 'left',
            ellipsis: !isMobile, // mobile cell is a custom flex (name+id+actions); antd ellipsis clips it wrong
            sortOrder: sortedInfo.columnKey === 'name' ? sortedInfo.order : null,
            sorter: (a, b) => {
                const ka = lastNameKey(a.name), kb = lastNameKey(b.name);
                return ka < kb ? -1 : ka > kb ? 1 : 0;
            },
            render: (v, record) => isMobile ? (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 4 }}>
                    <div style={{ minWidth: 0 }}>
                        {editableText(record, 'name', v, true)}
                        <div style={{ color: 'var(--ink-4)', fontSize: 11, marginTop: 1 }}>
                            {record.student_id || '—'}
                        </div>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, flexShrink: 0 }}>
                        <Button
                            type="text" size="small" icon={<IdcardOutlined />}
                            onClick={() => {
                                setProfileTarget(record);
                                // Current = first non-archived class; never show an old one here.
                                const currentCls = (record.classes || []).find(c => !c.archived);
                                const cls = currentCls ? allClasses.find(c => c.class_id === currentCls.class_id) : null;
                                setProfileClassInfo(cls || null);
                                setProfileArchivedClasses((record.classes || []).filter(c => c.archived));
                                setProfileVisible(true);
                            }}
                        />
                        {canResetPasswords && (
                            <Popconfirm
                                title={`Đặt lại mật khẩu về mã học sinh?`}
                                okText="Đặt lại" cancelText="Hủy"
                                onConfirm={() => handleResetPassword(record)}
                            >
                                <Button type="text" size="small" icon={<KeyOutlined />} />
                            </Popconfirm>
                        )}
                        <Popconfirm
                            title={`Xóa "${record.name || record.student_id}"?`}
                            description="Học sinh sẽ bị xóa khỏi hệ thống và tất cả các lớp."
                            okText="Xóa" cancelText="Hủy"
                            okButtonProps={{ danger: true }}
                            onConfirm={() => handleDelete(record)}
                        >
                            <Button type="text" size="small" danger icon={<DeleteOutlined />} />
                        </Popconfirm>
                    </div>
                </div>
            ) : editableText(record, 'name', v, true),
        },
        {
            title: 'Mã học sinh',
            dataIndex: 'student_id',
            key: 'student_id',
            width: 130,
            responsive: ['md', 'lg', 'xl', 'xxl'],
            sortOrder: sortedInfo.columnKey === 'student_id' ? sortedInfo.order : null,
            sorter: (a, b) => (a.student_id || '').localeCompare(b.student_id || '', undefined, { numeric: true }),
            // Trial ids are read-only: editing student_id here does NOT re-hash the password (which
            // defaults to the id), so hand-editing HT26001 → ME26007 would leave a half-converted
            // student — new login, old password, still flagged trial. "Chuyển thành học sinh chính
            // thức" is the path that does all of it.
            render: (v, record) => userData?.role === ROLES.MANAGER && record.enrollment_status !== 'trial'
                ? editableText(record, 'student_id', v, false)
                : (v || <Text type="secondary">—</Text>),
        },
        {
            title: 'SĐT phụ huynh',
            dataIndex: 'parent_number',
            key: 'parent_number',
            width: 140,
            render: (v, record) => editableText(record, 'parent_number', v, false),
        },
        {
            title: 'Giới tính',
            dataIndex: 'gender',
            key: 'gender',
            width: 100,
            align: 'center',
            filters: [
                { text: 'Nam', value: 'male' },
                { text: 'Nữ', value: 'female' },
            ],
            onFilter: (value, record) => record.gender === value,
            render: (_, record) => editableGender(record),
        },
        {
            title: 'Trạng thái',
            key: 'enrollment_status',
            width: 130,
            align: 'center',
            filters: [
                { text: 'Đang học', value: 'active'    },
                { text: 'Kết thúc', value: 'inactive'  },
                { text: 'Bảo lưu',  value: 'suspended' },
                { text: 'Học thử',  value: 'trial'     },
            ],
            onFilter: (value, record) => (record.enrollment_status || 'active') === value,
            render: (_, record) => editableStatus(record),
        },
        {
            title: 'Lớp đang học',
            key: 'classes',
            width: 180,
            render: (_, record) => {
                // Active (current) classes first, archived (old) after.
                const classes = [...(record.classes || [])].sort(
                    (a, b) => (a.archived ? 1 : 0) - (b.archived ? 1 : 0)
                );
                // Can only add to active classes the student isn't already in.
                const available = allClasses.filter(
                    ac => !ac.archived && !classes.some(c => c.class_id === ac.class_id)
                );
                return (
                    <Space wrap size={[4, 4]}>
                        {classes.map(c => c.archived ? (
                            <Tooltip key={c.class_id} title="Lớp đã lưu trữ">
                                <span className="tag t-neutral">{c.class_name} (đã lưu trữ)</span>
                            </Tooltip>
                        ) : (
                            <Popconfirm
                                key={c.class_id}
                                title={`Xóa học sinh khỏi lớp "${c.class_name}"?`}
                                okText="Xóa"
                                cancelText="Hủy"
                                okButtonProps={{ danger: true }}
                                onConfirm={() => removeFromClass(record, c.class_id)}
                            >
                                <span className="tag t-info as-tag-remove">{c.class_name}</span>
                            </Popconfirm>
                        ))}

                        {addingClassFor === record._id ? (
                            <Select
                                autoFocus
                                size="small"
                                style={{ width: 160 }}
                                placeholder="Chọn lớp"
                                onChange={classId => addToClass(record, classId)}
                                onBlur={() => setAddingClassFor(null)}
                                notFoundContent="Không còn lớp nào"
                                showSearch
                                optionFilterProp="children"
                            >
                                {available.map(ac => (
                                    <Option key={ac.class_id} value={ac.class_id}>
                                        {ac.class_name}
                                    </Option>
                                ))}
                            </Select>
                        ) : (
                            <Tooltip title="Thêm vào lớp">
                                <button
                                    type="button"
                                    className="as-tag-add"
                                    onClick={() => setAddingClassFor(record._id)}
                                >
                                    <PlusOutlined />
                                </button>
                            </Tooltip>
                        )}
                    </Space>
                );
            },
            filters: [
                { text: 'Có lớp',      value: 'has'  },
                { text: 'Chưa có lớp', value: 'none' },
            ],
            onFilter: (value, record) =>
                value === 'has'
                    ? record.classes && record.classes.length > 0
                    : !record.classes || record.classes.length === 0,
        },
        {
            title: 'Thao tác',
            key: 'actions',
            width: 80,
            fixed: 'right',
            align: 'center',
            responsive: ['md', 'lg', 'xl', 'xxl'],
            render: (_, record) => (
                <Space size={4}>
                    <Tooltip title="Xem hồ sơ">
                        <Button
                            type="text"
                            size="small"
                            icon={<IdcardOutlined />}
                            onClick={() => {
                                setProfileTarget(record);
                                // Current = first non-archived class; never show an old one here.
                                const currentCls = (record.classes || []).find(c => !c.archived);
                                const cls = currentCls ? allClasses.find(c => c.class_id === currentCls.class_id) : null;
                                setProfileClassInfo(cls || null);
                                setProfileArchivedClasses((record.classes || []).filter(c => c.archived));
                                setProfileVisible(true);
                            }}
                        />
                    </Tooltip>
                    {canResetPasswords && (
                        <Popconfirm
                            title={`Đặt lại mật khẩu của "${record.name || record.student_id}" về mã học sinh?`}
                            okText="Đặt lại" cancelText="Hủy"
                            onConfirm={() => handleResetPassword(record)}
                        >
                            <Tooltip title="Đặt lại mật khẩu">
                                <Button type="text" size="small" icon={<KeyOutlined />} />
                            </Tooltip>
                        </Popconfirm>
                    )}
                    {canResetPasswords && record.enrollment_status === 'trial' && (
                        <Popconfirm
                            title={`Chuyển "${record.name || record.student_id}" thành học sinh chính thức?`}
                            description="Học sinh sẽ được cấp mã ME mới, mật khẩu đổi theo mã mới và bị đăng xuất."
                            okText="Chuyển" cancelText="Hủy"
                            onConfirm={() => handleConvert(record)}
                        >
                            <Tooltip title="Chuyển thành học sinh chính thức">
                                <Button type="text" size="small" icon={<UserSwitchOutlined />} />
                            </Tooltip>
                        </Popconfirm>
                    )}
                    <Popconfirm
                        title={`Xóa "${record.name || record.student_id}"?`}
                        description="Học sinh sẽ bị xóa khỏi hệ thống và tất cả các lớp."
                        okText="Xóa"
                        cancelText="Hủy"
                        okButtonProps={{ danger: true }}
                        onConfirm={() => handleDelete(record)}
                    >
                        <Tooltip title="Xóa">
                            <Button type="text" size="small" danger icon={<DeleteOutlined />} />
                        </Tooltip>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    // ── Render ────────────────────────────────────────────────────────────────
    return (
        <div className="att2-page">
            <div className="att2-head">
                <div className="att2-head-l">
                    <h1 className="att2-title">Quản lý học sinh</h1>
                    <div className="att2-sub">Nhấp đúp vào ô tên, mã học sinh, số điện thoại hoặc giới tính để chỉnh sửa trực tiếp</div>
                </div>
                <div className="att2-tools">
                    <Input
                        prefix={<SearchOutlined style={{ color: 'var(--ink-4)' }} />}
                        placeholder="Tìm theo tên, mã học sinh, số điện thoại..."
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        style={{ maxWidth: 260 }}
                        allowClear
                    />
                    <Tooltip title="Tải lại">
                        <Button icon={<ReloadOutlined />} onClick={load} loading={loading} />
                    </Tooltip>
                    <Tooltip title="Xuất danh sách ra CSV (bao gồm thông tin lớp)">
                        <Button icon={<DownloadOutlined />} onClick={exportCSV}>
                            Xuất CSV
                        </Button>
                    </Tooltip>
                    <input
                        ref={bulkInputRef}
                        type="file"
                        multiple
                        accept="image/*"
                        style={{ display: 'none' }}
                        onChange={handleBulkFiles}
                    />
                    <Tooltip title="Đặt tên mỗi ảnh theo mã học sinh (VD: ME261.jpg) rồi chọn nhiều ảnh cùng lúc">
                        <Button
                            icon={<UserOutlined />}
                            loading={bulkBusy}
                            onClick={() => bulkInputRef.current && bulkInputRef.current.click()}
                        >
                            Tải ảnh hàng loạt
                        </Button>
                    </Tooltip>
                    <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
                        Thêm học sinh
                    </Button>
                </div>
            </div>

            <div className="stat-strip">
                <span className="stat"><span className="l">Tổng</span><span className="v">{students.length}</span></span>
                <span className="stat"><span className="l">Đang học</span><span className="v tone-ok">{countActive}</span></span>
                <span className="stat"><span className="l">Kết thúc</span><span className="v tone-bad">{countInactive}</span></span>
                <span className="stat"><span className="l">Bảo lưu</span><span className="v tone-neutral">{countSuspended}</span></span>
                <span className="stat"><span className="l">Học thử</span><span className="v tone-info">{countTrial}</span></span>
                <span className="stat"><span className="l">Có lớp</span><span className="v tone-ok">{withClass}</span></span>
                <span className="stat"><span className="l">Chưa có lớp</span><span className={`v ${withoutClass > 0 ? 'tone-warn' : ''}`}>{withoutClass}</span></span>
            </div>

            <Table
                    columns={columns}
                    dataSource={trialLast(filtered).map(s => ({ ...s, key: s._id }))}
                    loading={loading}
                    size="middle"
                    onChange={(_, __, sorter) => {
                        const s = Array.isArray(sorter) ? sorter[0] : sorter;
                        setSortedInfo(s?.order ? s : {});
                    }}
                    pagination={{ defaultPageSize: 20, showSizeChanger: true, pageSizeOptions: ['10','20','50'], showTotal: total => `${total} học sinh` }}
                    scroll={{ x: 900 }}
                    locale={{ emptyText: 'Không có học sinh nào' }}
                />

            <div className="att2-weeklabel">{filtered.length} / {students.length} học sinh hiển thị</div>

            {/* ── Profile Card Modal ─────────────────────────────────── */}
            <Modal
                title="Hồ sơ học sinh"
                visible={profileVisible}
                onCancel={() => setProfileVisible(false)}
                footer={null}
                width={680}
                bodyStyle={{ paddingTop: 8 }}
            >
                <StudentProfileCard
                    data={profileTarget}
                    classInfo={profileClassInfo}
                    archivedClasses={profileArchivedClasses}
                    onUpdate={handleProfileUpdate}
                    onClassUpdate={handleClassUpdate}
                />
            </Modal>

            {/* ── Create Modal ───────────────────────────────────────── */}
            <Modal
                title="Thêm học sinh mới"
                visible={createVisible}
                onOk={handleCreateSave}
                onCancel={() => { setCreateVisible(false); createForm.resetFields(); }}
                okText="Tạo tài khoản"
                cancelText="Hủy"
                confirmLoading={createLoading}
                forceRender
                width={480}
            >
                <Form form={createForm} layout="vertical" style={{ marginTop: 8 }}>
                    <Form.Item
                        name="name"
                        label="Họ và tên"
                        rules={[{ required: true, message: 'Vui lòng nhập tên học sinh' }]}
                    >
                        <Input placeholder="VD: Nguyễn Văn A" />
                    </Form.Item>
                    <Form.Item
                        name="is_trial"
                        label="Học thử"
                        valuePropName="checked"
                        extra={
                            <span style={{ color: 'var(--ink-4)', fontSize: 12 }}>
                                Học sinh học thử nhận mã HT26xxx. Khi vào học chính thức, dùng nút
                                “Chuyển thành học sinh chính thức” để cấp mã ME26xxx mới.
                            </span>
                        }
                    >
                        <Switch onChange={onCreateTrialToggle} />
                    </Form.Item>
                    <Form.Item
                        name="student_id"
                        label="Mã học sinh (ID đăng nhập)"
                        rules={[{ required: true, message: 'Vui lòng nhập mã học sinh' }]}
                        extra={
                            <span style={{ color: 'var(--ink-4)', fontSize: 12 }}>
                                <InfoCircleOutlined style={{ marginRight: 4 }} />
                                Mật khẩu mặc định sẽ giống mã học sinh này
                            </span>
                        }
                    >
                        <Input style={{ fontWeight: 600, letterSpacing: 1 }} readOnly />
                    </Form.Item>
                    <Form.Item
                        name="parent_number"
                        label="Số điện thoại phụ huynh"
                        rules={[{ required: true, message: 'Vui lòng nhập số điện thoại phụ huynh' }]}
                    >
                        <Input placeholder="VD: 0912345678" />
                    </Form.Item>
                    <Form.Item name="gender" label="Giới tính">
                        <Select allowClear placeholder="Chọn giới tính">
                            <Option value="male">Nam</Option>
                            <Option value="female">Nữ</Option>
                        </Select>
                    </Form.Item>
                    <Form.Item name="phone_number" label="Số điện thoại học sinh">
                        <Input placeholder="Tùy chọn" />
                    </Form.Item>
                    <Form.Item name="email" label="Email">
                        <Input placeholder="Tùy chọn" />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
}
